/**
 * Which frames are *data*, as opposed to metadata describing it.
 *
 * Lifted out of `ScreenerPanel` rather than living beside its only caller: React
 * Fast Refresh cannot refresh a module that exports both a component and a
 * plain function, so mixing them made every edit to the panel invalidate the
 * module instead of hot-reloading it. The symptom is worse than the lost
 * convenience — the page ends up running an old function body against new JSX,
 * which surfaces as an identifier being undefined that is plainly right there in
 * the source.
 */

import { COLUMNS_FRAME, type ScreenerFrame } from "@qufolio/transport";

import { PUSH_FRAME } from "./column-wave.js";
import { TIMINGS_FRAME } from "./timings.js";

/**
 * Rollup frames the server sends that the UI does not show yet.
 *
 * qv-data aggregates the screen by sector and by industry and ships both frames
 * whether or not anything renders them. They were tabs beside Symbols, and a
 * flat list of sectors is not what a sector tab is *for* — the useful shape is
 * drilling from a sector into the symbols inside it, which does not exist yet.
 * Until it does, two tabs that each answer a question nobody asked cost a row of
 * chrome above every screen.
 *
 * Hidden here rather than dropped in the transport, so the frames still arrive
 * and re-enabling the tabs is deleting these two names. See the drill-down item
 * in [`docs/TODO.md`](../../../../../docs/TODO.md).
 */
const HIDDEN_FRAMES: ReadonlySet<string> = new Set(["sector", "industry"]);

/**
 * The frames that are tabs — everything except column metadata and the rollups
 * that are not shown yet.
 *
 * `columns` describes the other frames rather than being one: it is per-column
 * where they are per-row. Rendering it as a tab shows a grid of metadata, and
 * applying a data frame's column view to it hides every column. Beyond that and
 * `HIDDEN_FRAMES`, nothing is special-cased, so a genuinely new *data* frame
 * from a newer server still becomes a tab with no code change.
 */
/**
 * The other two frames that describe the payload rather than being it.
 *
 * `push` says which fields this payload owns and which wave they are;
 * `timings` is the server's stage breakdown. Both are per-something-else rather
 * than per-row, so a data frame's column view names none of their fields and
 * the tab renders **blank** — a tab that promises content and delivers an empty
 * pane, which is worse than not offering it.
 *
 * They were never excluded because the rule was written when `columns` was the
 * only metadata frame; these two have been silently producing empty tabs on
 * every screen since. Reported from the browser 2026-09-11 as "nothing in the
 * timings tab", and the timings a user actually wants are in
 * `ScreenerTimingsPanel`, which reads the same frame properly.
 */
const META_FRAMES: ReadonlySet<string> = new Set([COLUMNS_FRAME, PUSH_FRAME, TIMINGS_FRAME]);

export function dataFramesOf(frames: readonly ScreenerFrame[]): ScreenerFrame[] {
  return frames.filter((f) => !META_FRAMES.has(f.name) && !HIDDEN_FRAMES.has(f.name));
}
